import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Download, FileText, FileJson, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NotesExporterProps {
  roadmapId: string | null;
  user: User | null;
  onSignInRequired: () => void;
}

export const NotesExporter = ({
  roadmapId,
  user,
  onSignInRequired,
}: NotesExporterProps) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: "markdown" | "json") => {
    if (!user) {
      onSignInRequired();
      return;
    }

    if (!roadmapId) {
      toast.error("No roadmap selected");
      return;
    }

    try {
      setExporting(true);

      const { data, error } = await supabase.functions.invoke("export-notes", {
        body: { roadmapId, format },
      });

      if (error) throw error;

      // Create blob and download
      const blob = new Blob([typeof data === "string" ? data : JSON.stringify(data, null, 2)], {
        type: format === "markdown" ? "text/markdown" : "application/json",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `roadmap_notes.${format === "markdown" ? "md" : "json"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Notes exported as ${format.toUpperCase()}! 📥`);
    } catch (error: any) {
      console.error("Error exporting notes:", error);
      toast.error(error.message || "Failed to export notes");
    } finally {
      setExporting(false);
    }
  };

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Notes
          </CardTitle>
          <CardDescription>
            Sign in to export your learning notes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={onSignInRequired} className="w-full">
            Sign In
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Export Notes
        </CardTitle>
        <CardDescription>
          Download all your notes for offline review
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={exporting} className="w-full">
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export Notes
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => handleExport("markdown")}>
              <FileText className="h-4 w-4 mr-2" />
              Export as Markdown (.md)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport("json")}>
              <FileJson className="h-4 w-4 mr-2" />
              Export as JSON (.json)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <p className="text-xs text-muted-foreground mt-3 text-center">
          Export includes all your daily notes and reflections
        </p>
      </CardContent>
    </Card>
  );
};
